/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1779235200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "twitter",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to X",
          "depth": 0,
          "id": "connect-to-x"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Syncing conversion events",
          "depth": 1,
          "id": "syncing-conversion-events"
        }, {
          "value": "Template mappings",
          "depth": 1,
          "id": "template-mappings"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      ol: "ol",
      li: "li",
      code: "code",
      h3: "h3",
      em: "em",
      h4: "h4",
      ul: "ul",
      img: "img",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {ArrayFanOutTip, EventSyncWarning, MatchRatesExplanation, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Build more robust campaigns on X (formerly Twitter) with up-to-date customer data from your data warehouse."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-custom-audiences",
                children: _jsx(_components.strong, {
                  children: "Custom audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Create audience lists and keep them up-to-date in X"
            }), _jsx(_components.td, {
              children: "Add"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversion-events",
                children: _jsx(_components.strong, {
                  children: "Conversion events"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync web conversion events to X"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-x",
        children: "Connect to X"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/destinations?from=docs",
            children: [_jsx(_components.strong, {
              children: "Destinations"
            }), " overview page"]
          }), " and click the ", _jsx(_components.strong, {
            children: "Add destination"
          }), " button."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "X"
          }), " in the destination catalog and click ", _jsx(_components.strong, {
            children: "Continue"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Proceed with the OAuth steps until you are redirected to the Hightouch website."
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, “X Production” and ", _jsx(_components.code, {
            children: "x-production"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your X destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports ", _jsx(_components.em, {
          children: "Segment Mode"
        }), ". New users in the model will be inserted into the X\nCustom Audience. Users that are removed from the model will be removed from the\nCustom Audience."]
      }), "\n", _jsx(_components.h4, {
        id: "x-account-selection",
        children: "X account selection"
      }), "\n", _jsx(_components.p, {
        children: "In this section, select the X account you would like to sync your data to."
      }), "\n", _jsx(_components.h4, {
        id: "audience-selection",
        children: "Audience selection"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports syncing to an ", _jsx(_components.strong, {
          children: "existing audience"
        }), " or ", _jsx(_components.strong, {
          children: "creating a new audience"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["To use an ", _jsx(_components.strong, {
          children: "existing segment"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.code, {
            children: "Use existing audience"
          }), " from the radio buttons."]
        }), "\n", _jsx(_components.li, {
          children: "Select the name of the audience from the dropdown menu."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ", _jsx(_components.strong, {
          children: "create a new audience"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.code, {
            children: "Create a new audience"
          }), " from the radio buttons."]
        }), "\n", _jsx(_components.li, {
          children: "Specify a custom name for this audience in the input field. Otherwise Hightouch will use the name of the model as the audience's name."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported X fields."
      }), "\n", _jsxs(_components.p, {
        children: ["X allows mapping to various user identifiers, such as Email, Device ID, X Handle, etc. X requires ", _jsx(_components.em, {
          children: "all fields"
        }), " for users\nto be both ", _jsx(_components.strong, {
          children: "normalized"
        }), " and ", _jsx(_components.strong, {
          children: "hashed"
        }), " using SHA256 (with the exception of Partner User ID)."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can achieve this in your model with a function\nby using the ", _jsx(_components.code, {
          children: "encode(digest(value, 'sha256'),'hex')"
        }), " function in Postgres, or refer to the ", _jsx(_components.a, {
          href: "/destinations/X#template-mappings",
          children: "Template Mappings"
        }), " Section in this documentation to learn about how Hightouch can handle hashing these records for you."]
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "X"
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports syncing ", _jsx(_components.em, {
          children: "Web Conversion Events"
        }), "."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "x-account-selection-1",
        children: "X account selection"
      }), "\n", _jsx(_components.p, {
        children: "In this section, select the X account you would like to sync your data to."
      }), "\n", _jsx(_components.h4, {
        id: "pixel-id-universal-website-tag-account-section",
        children: "Pixel ID (Universal Website Tag) account section"
      }), "\n", _jsxs(_components.p, {
        children: ["If the ", _jsx(_components.strong, {
          children: "Pixel ID"
        }), " exist in your model, toggle ", _jsx(_components.strong, {
          children: "Use Column"
        }), ", and select the appropriate column from the dropdown menu."]
      }), "\n", _jsx(_components.p, {
        children: "To retrieve your Pixel ID:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Navigate to your ", _jsx(_components.a, {
            href: "https://business.x.com/en/help/campaign-measurement-and-analytics/conversion-tracking-for-websites",
            children: "Events Manager"
          }), " page."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-x-pixel-id.png",
          alt: "Where to find Pixel ID"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Copy the ID and paste it in the Hightouch sync form."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "event-selection",
        children: "Event selection"
      }), "\n", _jsxs(_components.p, {
        children: ["Given the correct ", _jsx(_components.strong, {
          children: "Pixel ID"
        }), ", Hightouch will filter for the eligible events that allow conversion events to be synced to."]
      }), "\n", _jsxs(_components.p, {
        children: ["Select the event you'd like to sync to from the dropdown menu. If the ", _jsx(_components.strong, {
          children: "ID"
        }), " for these events exist in your model, toggle ", _jsx(_components.strong, {
          children: "Use Column"
        }), ", and select the appropriate column from the dropdown menu."]
      }), "\n", _jsx(_components.h4, {
        id: "identifier-section",
        children: "Identifier section"
      }), "\n", _jsx(_components.p, {
        children: "When syncing conversion events, X expects one identifier at minimum, or you can provide both."
      }), "\n", _jsx(_components.p, {
        children: "Select your choice for the sync from the radio buttons. This will render the fields in the mappings section, which you can map to from your model."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to sync columns from your source to the supported X fields."
      }), "\n", _jsxs(_components.p, {
        children: ["If you wish to use the ", _jsx(_components.strong, {
          children: "Email Address"
        }), " as an identifier, X expects it to be hashed using SHA256.\nYou can achieve this in your model with a function by using the encode(digest(value, 'sha256'),'hex') function in Postgres, or refer to the ", _jsx(_components.a, {
          href: "/destinations/X#template-mappings",
          children: "Template Mappings"
        }), " section in this documentation to learn about how Hightouch can handle hashing these records for you."]
      }), "\n", _jsx(_components.h3, {
        id: "template-mappings",
        children: "Template mappings"
      }), "\n", _jsx(_components.p, {
        children: "You can leverage Hightouch's Template Mappings feature to allow Hightouch to handle hashing certain fields for you automatically."
      }), "\n", _jsx(_components.p, {
        children: "To use this feature:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Template"
          }), " in the mappings window."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the column you would like to hash from the ", _jsx(_components.strong, {
            children: "Variables"
          }), " section."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "sha256"
          }), " from the ", _jsx(_components.strong, {
            children: "Functions"
          }), " section."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Apply"
          }), " to save."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-x-template-mappings.png",
          alt: "Template Mappings Instruction"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To find out more about Template Mapping and other Mapping features, visit our ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "Mapping Data"
        }), " section."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the audiences sync type."
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "X"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "400-invalid_parameter",
        children: "400: INVALID_PARAMETER"
      }), "\n", _jsx(_components.p, {
        children: "The full error message looks something like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"request\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"params\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"account_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"01ab234cdef\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"custom_audience_id\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"0a1b2\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"operation_type\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Update\""
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"operation_errors\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "["
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"code\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"INVALID_PARAMETER\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"message\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Tailored audience attribute value can not be empty.\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n        ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"parameter\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"\""
          }), "\n      ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "]"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This error occurs if any of your ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "field mappings"
        }), " include ", _jsx(_components.code, {
          children: "null"
        }), " values.\nX doesn't accepted hashed ", _jsx(_components.code, {
          children: "null"
        }), " values.\nTo resolve this, ", _jsxs(_components.a, {
          href: "/models/sql-editor#exclude-rows-with-null-values",
          children: ["remove rows containing ", _jsx(_components.code, {
            children: "null"
          }), " values"]
        }), " from your model's query results or enable the ", _jsxs(_components.a, {
          href: "/syncs/mapping-data#dont-sync-null-values",
          children: [_jsx(_components.strong, {
            children: "Don't sync null values"
          }), " option"]
        }), " in the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#advanced-mapper",
          children: "advanced mapper"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
